import { TmcContextService } from './../../../../../common/tmc-context.service';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { RetrievalStrategy, Station, StationType, TransportCreationProfile, TransportCreationProfileToStation } from '../../model/station.model';
import { DialogService, SelectFieldOption, TranslationService } from '@sdbn/common';
import { NgForm, ControlContainer } from '@angular/forms';
import { StructureSelection } from '../,,/../../../../../common/structure-selection/structure-selection.model';

import { StructureSelectionService } from '../,,/../../../../../common/structure-selection/structure-selection-dialog/structure-selection-dialog-service';
import { Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { MatDialog } from '@angular/material/dialog';
import { TableSelectionDialogComponent, TableSelectionDialogData } from '@sdbn/tmc-common';
import { TmcTableModel } from '@sdbn/common';
import { Row } from '@sdbn/common';
import { SelectionType } from '@sdbn/common';
import { ColumnType } from '@sdbn/common';
import { StationService } from '../../service/station.service';

@Component({
    selector: 'app-station-details',
    templateUrl: './station-details.component.html',
    styleUrls: ['./station-details.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    standalone: false
})
export class StationDetailsComponent implements OnInit, OnDestroy {

  possibleRetrievalStrategies = [
    new SelectFieldOption({ label: '', value: null }),
    new SelectFieldOption({ labelDictionaryKey: 'tmc-yard.common.enum.retrievalStrategy.FIFO', value: RetrievalStrategy.FIFO }),
    new SelectFieldOption({ labelDictionaryKey: 'tmc-yard.common.enum.retrievalStrategy.LIFO', value: RetrievalStrategy.LIFO })];

  tmcContextSubscription: Subscription;

  bufferZoneName: string;
  station: Station;


  @Input() form: NgForm;
  @Input("station") set setStation(station: Station) {
    this.station = station;
    this.updateBufferZoneName();
  }
  @Input() isLoading: boolean;

  // keys for type options
  keys = Object.keys;

  stationTypes = StationType;

  get componentsForPlannedAssignmentVisible(): boolean {
    return this.station?.plannedTransportAssignmentPossible;
  }


  constructor(
    public translationService: TranslationService,
    private tmcContextService: TmcContextService,
    private structureSelectionService: StructureSelectionService,
    private dialog: MatDialog,
    private stationService: StationService,
    private dialogService: DialogService
  ) {
    this.tmcContextSubscription = this.tmcContextService.updated.subscribe(() => this.updateBufferZoneName());
  }

  ngOnInit() {

  }

  ngOnDestroy(): void {
    if (this.tmcContextSubscription) {
      this.tmcContextSubscription.unsubscribe();
    }
  }

  assignBufferZone() {

    const structureSelection = new StructureSelection();

    structureSelection.stationId = this.station.bufferZoneId;

    this.structureSelectionService.selectWaitingArea(structureSelection).subscribe(
      value => {

        if (value != null) {
          const selection: StructureSelection = value;
          if (selection.stationId != null) {
            this.station.bufferZoneId = selection.stationId;
            this.updateBufferZoneName();
            this.markFormControlAsDirty('bufferZoneId');
          }
        }
      }
    );
  }

  private updateBufferZoneName() {
    if (this.station?.bufferZoneId != null) {
      const station = this.tmcContextService.findStationById(this.station.bufferZoneId);
      this.bufferZoneName = station?.name;
    } else {
      this.bufferZoneName = "";
    }
  }

  private markFormControlAsDirty(fcName: string) {
    if (this.form.form.controls[fcName]) {
      this.form.form.controls[fcName].markAsDirty();
      this.form.form.controls[fcName].markAsTouched();
      this.form.form.controls[fcName].updateValueAndValidity();
    }
  }

  public removeTransportCreationProfile(profile: TransportCreationProfileToStation) {


    this.dialogService.showConfirm(
      'tmc-yard.ui.admin.stations.transport-creation-profiles-confirm-remove-title',
      'tmc-yard.ui.admin.stations.transport-creation-profiles-confirm-remove-description'
    ).afterClosed().subscribe(confirm => {
      if (confirm) {
        if (profile.id) {
          this.station.deletedTransportCreationProfiles.push(profile);
        }

        const index = this.station.transportCreationProfiles.findIndex(p => p.id === profile.id);
        if (index > -1) {
          this.station.transportCreationProfiles.splice(index, 1);
        }

        this.updatePositionsIndex();
        this.form.form.markAsDirty();
      }
    });

  }

  public dropProfile(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.station.transportCreationProfiles, event.previousIndex, event.currentIndex);
    this.updatePositionsIndex();
    this.form.form.markAsDirty();
  }

  public addTransportCreationProfile() {
    this.stationService.getTransportCreationProfiles().subscribe(
      profiles => this.openTransportCreationProfileSelectionDialog(profiles)
    );
  }

  private openTransportCreationProfileSelectionDialog(availableTransportCreationProfiles: Array<TransportCreationProfile>) {

    const assignedProfileIds = this.station.transportCreationProfiles.map(profile => profile.id);
    const assignableProfiles = availableTransportCreationProfiles.filter(profile => assignedProfileIds.indexOf(profile.id) === -1);

    const tableSelectionDialogData: TableSelectionDialogData = {
      title: this.translationService.translate('tmc-yard.ui.admin.stations.transport-creation-profiles-selection-title'),
      tableModel: this.createTableModelForTransportCreationProfileSelectionDialog(assignableProfiles),
      confirmCallback: (tableModel: TmcTableModel) => {
        this.addTransportCreationProfileToStation(tableModel.selectedRows);
      }
    };

    this.dialog.open(TableSelectionDialogComponent, {
      data: tableSelectionDialogData,
      panelClass: 'tmc-dialog-container'
    });
  }

  private addTransportCreationProfileToStation(selectedRows: Row[]) {
    const selectedProfiles = this.convertRowsToTransportCreationProfileToStation(selectedRows);
    this.station.transportCreationProfiles.push(...selectedProfiles);
    this.updatePositionsIndex();
    this.form.form.markAsDirty();
  }

  private updatePositionsIndex() {
    for (let i = 0; i < this.station.transportCreationProfiles.length; i++) {
      this.station.transportCreationProfiles[i].index = i;
    }
  }

  private createTableModelForTransportCreationProfileSelectionDialog(assignableTransportCreationProfiles: TransportCreationProfile[]): TmcTableModel {
    const tableModel = new TmcTableModel();
    tableModel.selectionType = SelectionType.MULTIPLE;
    tableModel.sortable = true;
    tableModel.filterable = true;
    tableModel.sortColumn = 'name';
    tableModel.sortDirection = 'asc';
    tableModel.columnDefinitions = [
      { id: 'name', name: 'name', visible: true, type: ColumnType.STRING, translationKey: 'tmc-yard.entity.transport-creation-profile.name' }
    ];
    tableModel.setRows(this.convertTransportCreationProfileToRows(assignableTransportCreationProfiles));
    return tableModel;
  }


  private convertTransportCreationProfileToRows(transportCreationProfiles: TransportCreationProfile[]): Row[] {
    const rows: Row[] = [];
    for (const profile of transportCreationProfiles) {
      const row: Row = new Row();
      row.id = profile.id + '';
      row.values = {
        id: profile.id,
        name: profile.name
      };

      rows.push(row);
    }
    return rows;
  }

  private convertRowsToTransportCreationProfileToStation(selectedRows: Row[]): TransportCreationProfileToStation[] {
    return selectedRows.map(row => {
      const profile: TransportCreationProfileToStation = new TransportCreationProfileToStation();
      profile.id = null;
      profile.transportCreationProfileId = row.values.id;
      profile.displayName = row.values.displayName;
      return profile;
    });
  }

}
